import { getLocaliceKeyFromErrorCode } from "../services/error-codes"
import HeapsError from "./heaps-error"
import { TFunction } from "i18next"
import { useEffect } from "react"
import { useGlobalModals } from "../context/GlobalModalsContext"
import { useI18next } from "gatsby-plugin-react-i18next"
import { routes } from "../routes"
import { translationKeys } from "./translationKeys"
import { captureException } from "../services/exception-logger"

type Error = {
  code: string
  detail?: string
}

// this is format that comes from our backend
type ErrorResponse = {
  errors: Error[]
}

const fallBackErrorMessage = "An unexpected error occurred"

export const fallbackError = {
  errorMessage: fallBackErrorMessage,
  translationKey: "",
  errorCode: "unknown_error",
}

export const extractErrorMessageErrorCodeAndTranslationKey = (
  apiError: ErrorResponse | undefined
) => {
  if (!apiError || !apiError.errors || !apiError.errors.length) {
    return fallbackError
  }
  const errorCode = apiError.errors[0].code
  const errorDetail = apiError.errors[0].detail
  const localiceKey = getLocaliceKeyFromErrorCode(errorCode)
  // prio for error message: 1) detail, 2) translation key, 3) general fallback
  const errorMessage = errorDetail ? errorDetail : ""
  const translationKey = errorMessage ? "" : localiceKey ? localiceKey : ""
  return { errorMessage, translationKey, errorCode }
}

export const getErrorMessage = (error: unknown, t: TFunction) => {
  if (!(error instanceof HeapsError)) {
    // Everything our fetch layer throws is a HeapsError, so anything else
    // reaching user-facing error handling is a bug on our side
    captureException(error, {
      type: "business_logic",
      category: "invalid-state",
      context: { errorSource: "getErrorMessage - Not HeapsError" },
    })
    // Return a fallback message. This is not something we called ourselves
    return fallBackErrorMessage
  }

  if (error.errorMessage) {
    return error.errorMessage
  }
  if (error.translationKey) {
    return t(error.translationKey)
  }
  return fallBackErrorMessage
}

export const getErrorCode = (error: unknown) => {
  if (!(error instanceof HeapsError)) {
    captureException(error, {
      type: "business_logic",
      category: "invalid-state",
      context: { errorSource: "getErrorCode - Not HeapsError" },
    })
    // Return a fallback message. This is not something we called ourselves
    return ""
  }

  return error.errorCode
}

export const useHandleFetchError = (
  error: unknown,
  t: TFunction,
  navigateTo404: boolean = true
) => {
  const { setInfoModalConfig } = useGlobalModals()
  const { navigate } = useI18next()

  return useEffect(() => {
    if (!error) {
      return
    }
    const errorMessage = getErrorMessage(error, t)
    setInfoModalConfig({
      title: t(translationKeys.comboPaymentErrorGenericHeader),
      description: errorMessage,
      onOkayClick: () => {
        if (!navigateTo404) {
          return
        }
        navigate(routes.notFound)
      },
    })
  }, [error])
}
